// thread — the chat thread as the reader sees it, pure (pen "Chat – every
// entry"). The chat is a log of the conversation; the work lives at the
// object. Every entry has one of three WEIGHTS:
//   - conversation: your message, the assistant's reply (full text);
//   - card: things that wait for you (a question, a plan, a proposal);
//   - trail: tool activity, notices, a material made, an export ready (one
//     quiet line, details one click away).
// `projectThread` turns the transcript's entries into the rows the thread
// renders: consecutive trail lines merge into one, a message the server never
// got folds its "not sent" notice into the bubble, and a thread spanning days
// gets a quiet divider per day. A client projection only: the log is
// untouched, so the same entries can always be shown another way.

export type { MergeableEntry, ThreadDay, ThreadItem, TrailPart };
export {
    canSendAgain,
    entryWeight,
    firstNewEntryId,
    isWaiting,
    lastTurn,
    latestTimestamp,
    projectThread,
    threadItemEntries,
    trailParts,
    turnMadeSomething,
};

import { exhaustSwitchCase } from "@bilda/utils";
import { toDisplay } from "../../app/externalError.js";
import { entryKey } from "./presentation.js";
import type {
    AgentActivityEntry,
    AuthorEntry,
    ChatEntry,
    ExportEntry,
    MaterialEntry,
    SystemEntryKind,
    UnknownEntry,
} from "./types.js";

type EntryWeight = "conversation" | "card" | "trail";

const entryWeight = (entry: ChatEntry): EntryWeight => {
    switch (entry.type) {
        case "author":
        case "agent":
            return "conversation";
        // A question waits as a card; answered or skipped, it is one line.
        case "information_request":
        case "proposal":
            return entry.status.type === "pending" ? "card" : "trail";
        case "plan":
        case "information_request_pending":
        case "material_building":
        case "action_offer":
            return "card";
        case "agent_activity":
        case "waiting_change":
        case "material":
        case "export":
        case "system":
        case "information_submission":
        case "unknown":
            return "trail";
        default:
            return exhaustSwitchCase(entry);
    }
};

// A card that needs the reader before the work goes on ("Waiting for you").
// Plans join once the thread knows when they are settled.
const isWaiting = (entry: ChatEntry): boolean =>
    (entry.type === "information_request" || entry.type === "proposal") &&
    entry.status.type === "pending";

// The trail entries that fold into a merged line. Notices stay their own
// line: each carries its own way forward (Continue, Try again).
type MergeableEntry =
    | AgentActivityEntry
    | MaterialEntry
    | ExportEntry
    | Extract<ChatEntry, { readonly type: "information_submission" }>
    | UnknownEntry;

const asMergeable = (entry: ChatEntry): MergeableEntry | undefined => {
    switch (entry.type) {
        case "agent_activity":
        case "material":
        case "export":
        case "information_submission":
        case "unknown":
            return entry;
        default:
            return undefined;
    }
};

type ThreadDay =
    | { readonly type: "today" }
    | { readonly type: "yesterday" }
    | { readonly type: "earlier"; readonly epochMs: number };

type ThreadItem =
    | {
          readonly type: "entry";
          readonly key: string;
          readonly entry: ChatEntry;
      }
    // Your message the server never got: the bubble plus its notice, as one
    // row. `noticeId` is the folded notice's id (the transcript offers Try
    // again only on its tail).
    | {
          readonly type: "unsent_message";
          readonly key: string;
          readonly entry: AuthorEntry;
          readonly noticeId: string;
          readonly cause: SystemEntryKind;
      }
    | {
          readonly type: "trail_run";
          readonly key: string;
          readonly entries: readonly [
              MergeableEntry,
              MergeableEntry,
              ...MergeableEntry[],
          ];
      }
    | {
          readonly type: "day_divider";
          readonly key: string;
          readonly day: ThreadDay;
      }
    // "New since you were here": once, where you left off.
    | { readonly type: "new_since"; readonly key: string };

// The entries a row stands for, in order (for the turn head and tail checks).
const threadItemEntries = (item: ThreadItem): readonly ChatEntry[] => {
    switch (item.type) {
        case "entry":
            return [item.entry];
        case "unsent_message":
            return [item.entry];
        case "trail_run":
            return item.entries;
        case "day_divider":
        case "new_since":
            return [];
        default:
            return exhaustSwitchCase(item);
    }
};

// A notice saying the request never reached the server: the message before
// it was not sent.
const notSentCause = (entry: ChatEntry): SystemEntryKind | undefined => {
    if (entry.type !== "system") return undefined;
    const { kind } = entry;
    if (kind.type === "connection_lost" && !kind.messageSaved) return kind;
    if (kind.type === "failed" && kind.error !== undefined) return kind;
    return undefined;
};

const foldUnsent = (entries: readonly ChatEntry[]): readonly ThreadItem[] =>
    entries.flatMap((entry, i): readonly ThreadItem[] => {
        const next = entries[i + 1];
        const cause = next === undefined ? undefined : notSentCause(next);
        if (
            entry.type === "author" &&
            next !== undefined &&
            cause !== undefined
        )
            return [
                {
                    type: "unsent_message",
                    key: entryKey(entry),
                    entry,
                    noticeId: next.id,
                    cause,
                },
            ];
        const prev = entries[i - 1];
        if (notSentCause(entry) !== undefined && prev?.type === "author")
            return [];
        return [{ type: "entry", key: entryKey(entry), entry }];
    });

const mergeTrail = (items: readonly ThreadItem[]): readonly ThreadItem[] =>
    items.reduce<readonly ThreadItem[]>((out, item) => {
        const mergeable =
            item.type === "entry" ? asMergeable(item.entry) : undefined;
        if (mergeable === undefined) return [...out, item];
        const last = out.at(-1);
        const head = out.slice(0, -1);
        if (last?.type === "trail_run")
            return [
                ...head,
                { ...last, entries: [...last.entries, mergeable] },
            ];
        const lastMergeable =
            last?.type === "entry" ? asMergeable(last.entry) : undefined;
        if (lastMergeable === undefined) return [...out, item];
        return [
            ...head,
            {
                type: "trail_run",
                key: `trail:${entryKey(lastMergeable)}`,
                entries: [lastMergeable, mergeable],
            },
        ];
    }, []);

const DAY_MS = 24 * 60 * 60 * 1000;

// The local calendar day an instant falls on, as a sortable key.
const dayKey = (epochMs: number): string => {
    const d = new Date(epochMs);
    return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
};

const dayOf = (epochMs: number, now: number): ThreadDay => {
    const key = dayKey(epochMs);
    if (key === dayKey(now)) return { type: "today" };
    if (key === dayKey(now - DAY_MS)) return { type: "yesterday" };
    return { type: "earlier", epochMs };
};

const timestampOf = (item: ThreadItem): number | undefined =>
    threadItemEntries(item).reduce<number | undefined>(
        (found, entry) =>
            found ??
            (entry.type === "author" || entry.type === "agent"
                ? entry.timestamp
                : undefined),
        undefined,
    );

// One quiet divider per day, only once the thread spans more than one day
// (a thread from today needs no "Today").
const withDayDividers = (
    items: readonly ThreadItem[],
    now: number,
): readonly ThreadItem[] => {
    const days = new Set(
        items.flatMap((item) => {
            const at = timestampOf(item);
            return at === undefined ? [] : [dayKey(at)];
        }),
    );
    if (days.size < 2) return items;
    return items.reduce<{
        readonly out: readonly ThreadItem[];
        readonly day: string | undefined;
    }>(
        (acc, item) => {
            const at = timestampOf(item);
            if (at === undefined || dayKey(at) === acc.day)
                return { ...acc, out: [...acc.out, item] };
            const divider: ThreadItem = {
                type: "day_divider",
                key: `day:${dayKey(at)}`,
                day: dayOf(at, now),
            };
            return { out: [...acc.out, divider, item], day: dayKey(at) };
        },
        { out: [], day: undefined },
    ).out;
};

const timeOf = (entry: ChatEntry): number | undefined =>
    entry.type === "author" || entry.type === "agent"
        ? entry.timestamp
        : undefined;

// The newest instant the thread shows (what you have now seen).
const latestTimestamp = (entries: readonly ChatEntry[]): number | undefined =>
    entries.reduce<number | undefined>((latest, entry) => {
        const at = timeOf(entry);
        return at !== undefined && (latest === undefined || at > latest)
            ? at
            : latest;
    }, undefined);

// Where "New since you were here" goes: the first entry newer than what you
// last saw, when you had seen part of the thread before. Undefined on a first
// visit and when nothing is new.
const firstNewEntryId = (
    entries: readonly ChatEntry[],
    seenUntil: number | undefined,
): string | undefined => {
    if (seenUntil === undefined) return undefined;
    const at = entries.findIndex((entry) => {
        const time = timeOf(entry);
        return time !== undefined && time > seenUntil;
    });
    return at > 0 ? entries[at]?.id : undefined;
};

// The marker goes right before the row holding the first new entry.
const withNewSince = (
    items: readonly ThreadItem[],
    newSinceId: string | undefined,
): readonly ThreadItem[] =>
    newSinceId === undefined
        ? items
        : items.flatMap((item): readonly ThreadItem[] =>
              threadItemEntries(item).some((e) => e.id === newSinceId)
                  ? [{ type: "new_since", key: "new-since" }, item]
                  : [item],
          );

const projectThread = (
    entries: readonly ChatEntry[],
    now: number,
    newSinceId?: string,
): readonly ThreadItem[] =>
    withNewSince(
        withDayDividers(mergeTrail(foldUnsent(entries)), now),
        newSinceId,
    );

// What a merged trail line names, in order: the run's tool activity as ONE
// part (its steps pooled, so a kind of work is named once), then materials
// and exports counted once, each where the first one sat.
type TrailPart =
    | {
          readonly type: "activity";
          readonly entries: readonly [
              AgentActivityEntry,
              ...AgentActivityEntry[],
          ];
      }
    | { readonly type: "materials_made"; readonly count: number }
    | { readonly type: "exports_ready"; readonly count: number }
    | { readonly type: "answered" }
    | { readonly type: "unknown" };

const trailParts = (
    entries: readonly MergeableEntry[],
): readonly TrailPart[] => {
    const count = (type: MergeableEntry["type"]) =>
        entries.filter((e) => e.type === type).length;
    return entries.flatMap((entry, i): readonly TrailPart[] => {
        const first = entries.findIndex((e) => e.type === entry.type) === i;
        switch (entry.type) {
            case "agent_activity":
                return first
                    ? [
                          {
                              type: "activity",
                              entries: [
                                  entry,
                                  ...entries
                                      .slice(i + 1)
                                      .filter(
                                          (e): e is AgentActivityEntry =>
                                              e.type === "agent_activity",
                                      ),
                              ],
                          },
                      ]
                    : [];
            case "material":
                return first
                    ? [{ type: "materials_made", count: count("material") }]
                    : [];
            case "export":
                return first
                    ? [{ type: "exports_ready", count: count("export") }]
                    : [];
            case "information_submission":
                return first ? [{ type: "answered" }] : [];
            case "unknown":
                return first ? [{ type: "unknown" }] : [];
            default:
                return exhaustSwitchCase(entry);
        }
    });
};

// Whether sending a message that never arrived again can help.
const canSendAgain = (cause: SystemEntryKind): boolean => {
    switch (cause.type) {
        case "connection_lost":
            return true;
        case "failed":
            return (
                cause.error === undefined || toDisplay(cause.error).retriable
            );
        default:
            return false;
    }
};

// The latest turn: everything after your last message.
const lastTurn = (entries: readonly ChatEntry[]): readonly ChatEntry[] =>
    entries.slice(
        entries.reduce(
            (last, entry, i) => (entry.type === "author" ? i : last),
            -1,
        ) + 1,
    );

// Whether the turn a notice closes made something (a material, an export, a
// proposal, a plan) before it ended: then "Nothing was changed" is untrue.
// The turn is everything after the last message of yours before the notice.
const turnMadeSomething = (
    entries: readonly ChatEntry[],
    noticeId: string,
): boolean => {
    const at = entries.findIndex(
        (entry) => entry.type === "system" && entry.id === noticeId,
    );
    if (at < 0) return false;
    const before = entries.slice(0, at);
    const opener = before.reduce(
        (last, entry, i) => (entry.type === "author" ? i : last),
        -1,
    );
    return before
        .slice(opener + 1)
        .some(
            (entry) =>
                entry.type === "material" ||
                entry.type === "export" ||
                entry.type === "proposal" ||
                entry.type === "plan",
        );
};
